import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, EyeOff } from 'lucide-react';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const LandingLoginModal = ({ open, onOpenChange, onForgotClick, onRegisterClick }) => {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const { t } = useTranslation();

  const [loginData, setLoginData] = useState({ email: '', password: '', totp_code: '' });
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleClose = (open) => {
    onOpenChange(open);
    if (!open) { setRequires2FA(false); setLoginData({ email: '', password: '', totp_code: '' }); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginData.email, loginData.password, loginData.totp_code || null);
      if (result?.requires_2fa) { setRequires2FA(true); setLoading(false); return; }
      toast.success(t('auth.welcomeBack'));
      handleClose(false);
      setTimeout(() => {
        if (result.role === 'admin') navigate('/admin/dashboard');
        else navigate('/dashboard');
      }, 100);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.includes('locked') ? detail : detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{t('landing.loginModal.title')}</DialogTitle>
        </DialogHeader>

        <button
          type="button"
          data-testid="google-signin-btn"
          onClick={async () => {
            try {
              await loginWithGoogle('/dashboard');
            } catch (error) {
              toast.error(t('auth.googleOAuthFailed'));
            }
          }}
          className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 hover:bg-background transition-colors"
        >
          <GoogleIcon />
          {t('landing.loginModal.googleSignIn') || 'Continue with Google'}
        </button>

        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400">{t('landing.loginModal.orEmail') || 'or continue with email'}</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <Label htmlFor="login-email">{t('landing.loginModal.email')}</Label>
            <Input
              id="login-email" type="email"
              value={loginData.email}
              onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
              required placeholder="you@company.com"
              data-testid="login-email-input"
              disabled={requires2FA}
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1">
              <Label htmlFor="login-password">{t('landing.loginModal.password')}</Label>
              <button type="button" data-testid="forgot-password-link"
                onClick={onForgotClick}
                className="text-xs text-primary hover:underline">
                {t('landing.loginModal.forgotPassword') || 'Forgot password?'}
              </button>
            </div>
            <div className="relative">
              <Input id="login-password" type={showPassword ? 'text' : 'password'}
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required data-testid="login-password-input"
                disabled={requires2FA}
                className="pr-10"
              />
              <button type="button" onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                tabIndex={-1} data-testid="toggle-modal-login-password" disabled={requires2FA}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {requires2FA && (
            <div className="p-4 bg-secondary rounded-lg border border-border">
              <Label htmlFor="totp-code" className="text-primary font-medium">
                {t('auth.twoFa.label')}
              </Label>
              <p className="text-sm text-primary mb-2">
                {t('auth.twoFa.desc')}
              </p>
              <Input
                id="totp-code" type="text"
                value={loginData.totp_code}
                onChange={(e) => setLoginData({ ...loginData, totp_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder="000000"
                className="text-center text-lg tracking-widest"
                maxLength={6} autoFocus
                data-testid="totp-code-input"
              />
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
            {loading
              ? <span className="flex items-center justify-center"><svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>{t('landing.loginModal.signingIn')}</span>
              : requires2FA ? t('auth.twoFa.verify')
              : t('landing.loginModal.signIn')}
          </Button>
          <p className="text-center text-sm text-slate-600">
            {t('landing.loginModal.noAccount')}{' '}
            <button type="button" onClick={onRegisterClick} className="text-primary hover:underline font-semibold">
              {t('landing.loginModal.signUpLink')}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LandingLoginModal;
